package fr.frux.feature.treeadd.wizard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.maps.Location
import fr.frux.core.media.PickedImage
import fr.frux.core.media.PreparedPhoto
import fr.frux.core.media.prepareForUpload
import fr.frux.feature.treeadd.TreeAddIntroStore
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.photos.TreePhotoUploads
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.getAndUpdate
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.updateAndGet
import kotlinx.coroutines.launch
import kotlin.uuid.Uuid

/**
 * Machine d'étapes de l'assistant de création (Placer, Espèce, Détails). Reste mince : les
 * étapes 2 et 3 et l'envoi sont confiés à des contrôleurs dédiés (detekt `TooManyFunctions`), qui
 * lisent les dépendances ci-dessous et agissent sur l'état par [updateState] et [emit].
 *
 * La [idempotencyKey] est tirée une fois à l'ouverture : un nouvel essai d'envoi, même après un
 * retour en arrière, rejoue la même création au lieu d'en produire une seconde.
 */
class TreeWizardViewModel(
    treeRepository: TreeRepository,
    speciesRepository: SpeciesRepository,
    internal val photoUploads: TreePhotoUploads,
    private val start: TreeWizardStart = TreeWizardStart(),
    private val introStore: TreeAddIntroStore? = null,
    /** Relecture du profil après création, voir `FirstContributionCheck`. Faux par défaut. */
    internal val isFirstContribution: suspend () -> Boolean = { false },
    internal val idempotencyKey: String = Uuid.random().toString(),
    internal val preparePhoto: suspend (PickedImage) -> PreparedPhoto = { it.prepareForUpload() },
    currentMonth: () -> Int = ::currentHarvestMonth,
) : ViewModel() {
    private val _state = MutableStateFlow(TreeWizardState())
    val state: StateFlow<TreeWizardState> = _state.asStateFlow()

    private val _events = Channel<TreeWizardEvent>(Channel.BUFFERED)
    val events: Flow<TreeWizardEvent> = _events.receiveAsFlow()

    /** Étape 2 : grille d'espèces, recherche, certitude. Les écrans l'appellent directement. */
    val species: SpeciesStepController =
        SpeciesStepController(
            speciesRepository = speciesRepository,
            treeRepository = treeRepository,
            updateState = { transform -> _state.update(transform) },
            scope = viewModelScope,
            currentMonth = currentMonth,
        )

    /** Étape 3 : état du jour, photos, note. Les écrans l'appellent directement. */
    val details: DetailsStepController =
        DetailsStepController(
            updateState = { transform -> _state.update(transform) },
            scope = viewModelScope,
            preparePhoto = preparePhoto,
        )

    /** Soumission : création à clé constante, photos, progression et sortie. */
    internal val submitter: WizardSubmitter =
        WizardSubmitter(
            treeRepository = treeRepository,
            photoUploads = photoUploads,
            scope = viewModelScope,
            state = _state,
            events = _events,
            idempotencyKey = idempotencyKey,
            preparePhoto = preparePhoto,
            isFirstContribution = isFirstContribution,
        )

    /**
     * Dernière cible de visée vue au démontage de l'étape 1 (remontage de l'écran par la
     * navigation). Non persistée : elle évite qu'un remontage ramène la caméra à la position figée.
     */
    internal var rememberedAim: Location? = null

    init {
        openIntro()
        species.loadReferential()
    }

    // updateState et emit sont destinés aux contrôleurs SpeciesStepController, DetailsStepController
    // et WizardSubmitter (exposés par ce ViewModel, appelés directement par les écrans).
    internal fun updateState(transform: (TreeWizardState) -> TreeWizardState) = _state.update(transform)

    internal fun emit(event: TreeWizardEvent) {
        _events.trySend(event)
    }

    fun confirmPosition(position: Location) {
        rememberedAim = null
        val entering = _state.value.step == WizardStep.PLACE
        updateState {
            if (it.step == WizardStep.PLACE) {
                it.copy(position = position, step = WizardStep.SPECIES, exitPrompt = null)
            } else {
                it
            }
        }
        if (entering) species.loadAround(position)
    }

    /**
     * « Un lieu » plutôt qu'« Un arbre » : l'assistant reste à l'étape 1, c'est composeApp qui le
     * remplace par la création de lieu (aucune dépendance vers `feature-site`).
     */
    fun chooseSite(position: Location) {
        emit(TreeWizardEvent.SiteChosen(position))
    }

    fun goToDetails() {
        updateState {
            if (it.step == WizardStep.SPECIES && it.species.canContinue) it.copy(step = WizardStep.DETAILS) else it
        }
    }

    fun goBack() {
        when (_state.value.submission) {
            is WizardSubmission.Uploading -> submitter.leaveNow()
            WizardSubmission.Creating -> Unit
            WizardSubmission.Idle -> stepBack()
        }
    }

    private fun stepBack() {
        when (_state.value.step) {
            WizardStep.DETAILS -> updateState { it.copy(step = WizardStep.SPECIES, exitPrompt = null) }
            WizardStep.SPECIES -> updateState { it.copy(step = WizardStep.PLACE, exitPrompt = null) }
            WizardStep.PLACE -> requestExit()
        }
    }

    fun requestExit() {
        when (_state.value.submission) {
            // L'arbre existe déjà : rien à perdre, la file d'envoi continue sans l'assistant.
            is WizardSubmission.Uploading -> submitter.leaveNow()

            // Création en vol : sortir ici perdrait l'arbre ou ses photos.
            WizardSubmission.Creating -> Unit

            WizardSubmission.Idle -> askExit()
        }
    }

    private fun askExit() {
        val after =
            _state.updateAndGet {
                val lost = it.lostInputs()
                // Aussi à l'étape Placer : les saisies survivent à un retour 3 → 2 → 1.
                if (lost.isEmpty) it else it.copy(exitPrompt = lost)
            }
        if (after.exitPrompt == null) emit(TreeWizardEvent.Exited)
    }

    fun confirmExit() {
        // Un double appui ne doit pas sortir deux fois : seul le premier trouve la confirmation.
        val hadPrompt = _state.getAndUpdate { it.copy(exitPrompt = null) }.exitPrompt != null
        if (hadPrompt) emit(TreeWizardEvent.Exited)
    }

    fun dismissExit() {
        updateState { it.copy(exitPrompt = null) }
    }

    fun acknowledgeIntro() {
        updateState { it.copy(introVisible = false) }
        val store = introStore ?: return
        viewModelScope.launch { store.markSeen() }
    }

    fun selectExistingTree(treeId: Long) {
        emit(TreeWizardEvent.ExistingTreeSelected(treeId))
    }

    fun submit() = submitter.submit()

    fun consumeSubmitError() {
        updateState { it.copy(submitFailed = false) }
    }

    private fun openIntro() {
        val store = introStore ?: return
        viewModelScope.launch {
            if (start.guideAlreadyRead) {
                // L'aide vient d'être lue depuis la carte : la montrer encore serait une redite.
                store.markSeen()
            } else if (!store.isSeen()) {
                updateState { it.copy(introVisible = true) }
            }
        }
    }
}
